import { useRef, useState } from "react"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import cx from "clsx"
import type { SessionTemplate } from "shared"
import PlusIcon from "@rawa/ui/assets/icon-plus.svg?react"
import SearchIcon from "../../../assets/icon-search.svg?react"
import { archiveSessionTemplate, fetchSessionTemplates } from "../api"
import { AdminButton, AdminButtonLink } from "@rawa/ui/AdminButton"
import { pathHelpers, paths } from "../../../paths"
import { useDebounce } from "../../../hooks/useDebounce"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import { isForbidden } from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./SessionTemplatesListPage.module.css"

// The same pause the template form waits before checking a name, so the two
// admin screens feel alike under typing.
const SEARCH_DEBOUNCE_MS = 400

export function SessionTemplatesListPage() {
  const [search, setSearch] = useState("")
  const settledSearch = useDebounce(search, SEARCH_DEBOUNCE_MS)
  const queryClient = useQueryClient()
  const [archived, setArchived] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const {
    data: sessionTemplates = [],
    isPending,
    isError,
    error,
    isPlaceholderData,
  } = useQuery({
    queryKey: ["session-templates", settledSearch],
    queryFn: ({ signal }) => fetchSessionTemplates(settledSearch, signal),
    // The list a term narrows stays on screen while the next one is fetched,
    // rather than the screen falling back to its skeleton on every keystroke.
    placeholderData: keepPreviousData,
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchSessionTemplates", forbiddenIsExpected: true },
  })

  // Spoken, not shown: the list changing under a search or an archive is
  // silent to a screen reader otherwise.
  const searchResult =
    isPending || isPlaceholderData || isError || settledSearch === ""
      ? ""
      : sessionTemplates.length === 0
        ? "No matches."
        : `${sessionTemplates.length} ${sessionTemplates.length === 1 ? "session template" : "session templates"}`
  const announcement = archived ? `Archived ${archived}.` : searchResult

  // A 403 is an authorization outcome with a screen of its own; anything else
  // is a failure to load.
  const forbidden = isError && isForbidden(error)
  const loadError = isError && !forbidden

  // Re-reading the list is what takes the row away, so a search that was in
  // flight when the archive landed cannot put it back.
  const archive = useMutation({
    mutationFn: (template: SessionTemplate) =>
      archiveSessionTemplate(template.id),
    onError: (err) => trackError(err, { source: "archiveSessionTemplate" }),
    onSuccess: (_result, archivedItem) => {
      setArchived(archivedItem.name)
      // The row it was on is going, so focus needs somewhere deliberate
      // to land. The search box is on screen whether or not any rows are.
      searchRef.current?.focus()
      return queryClient.invalidateQueries({ queryKey: ["session-templates"] })
    },
  })

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>Form administration</p>
          <h1 className={cx(typography.h2, styles.heading)}>
            Session templates
          </h1>
          <p className={styles.subtitle}>
            Assemble existing modules into a reusable session note.
          </p>
        </div>
        {/* A link, so it cannot be disabled while loading the way the other
            admin pages disable their action button — it waits instead. */}
        {!isPending && !forbidden && (
          <AdminButtonLink
            href={paths.newSessionTemplate}
            className={styles.addButton}
          >
            <PlusIcon className={styles.addIcon} aria-hidden="true" />
            Create template
          </AdminButtonLink>
        )}
      </div>

      {forbidden && <ErrorCallout>{NOT_AUTHORIZED_MESSAGE}</ErrorCallout>}

      {isPending && !isError && (
        <div
          role="status"
          aria-label="Loading session templates"
          className={styles.skeletonPanel}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.skeletonRow}>
              <span className={styles.skeletonName} />
            </div>
          ))}
        </div>
      )}

      {/* Left up through a failed load: changing the term is what asks
          again, so taking it away would strand the screen. */}
      {!isPending && !forbidden && (
        <label className={styles.searchField}>
          <SearchIcon className={styles.searchIcon} aria-hidden="true" />
          <input
            type="search"
            className={styles.search}
            aria-label="Search session templates"
            placeholder="Search templates by name or description"
            ref={searchRef}
            value={search}
            onChange={(e) => {
              setArchived(null)
              setSearch(e.target.value)
            }}
          />
        </label>
      )}

      {loadError && (
        <ErrorCallout>Failed to load session templates.</ErrorCallout>
      )}

      {archive.isError && archive.variables && (
        <ErrorCallout>
          Could not archive {archive.variables.name}. Please try again.
        </ErrorCallout>
      )}

      {!isPending &&
        !loadError &&
        !forbidden &&
        (sessionTemplates.length === 0 ? (
          <p className={styles.empty}>
            {settledSearch === "" ? "No session templates yet." : "No matches."}
          </p>
        ) : (
          <ul
            className={styles.list}
            aria-label="Session templates"
            aria-busy={isPlaceholderData}
          >
            {sessionTemplates.map((template) => (
              <li key={template.id} className={styles.card}>
                <span className={styles.name}>{template.name}</span>
                <AdminButtonLink
                  variant="outline"
                  href={pathHelpers.toSessionTemplate(template.id)}
                  className={styles.rowAction}
                  ariaLabel={`Edit ${template.name}`}
                >
                  Edit
                </AdminButtonLink>
                <AdminButton
                  variant="outline"
                  className={styles.rowAction}
                  onClick={() => archive.mutate(template)}
                  disabled={
                    archive.isPending && archive.variables.id === template.id
                  }
                  ariaLabel={`Archive ${template.name}`}
                >
                  {archive.isPending && archive.variables.id === template.id
                    ? "Archiving…"
                    : "Archive"}
                </AdminButton>
              </li>
            ))}
          </ul>
        ))}

      <span role="status" aria-live="polite" className={styles.srOnly}>
        {announcement}
      </span>
    </div>
  )
}
